import { known, unknown, type Maybe } from './types.js';
import type { CardioSegment } from './types.js';

/**
 * Cardio calculations.
 *
 * Kept entirely separate from strength volume. A 4 km run and a 100 kg squat
 * are both work, but expressing them in the same number would make them look
 * comparable when they are not.
 *
 * Distances are metres, durations seconds — integers throughout, matching the
 * schema.
 */

export type CardioUnknownReason = 'missing_distance' | 'missing_duration' | 'zero_distance';

/** Seconds per kilometre — the pace runners actually think in. */
export function paceSecondsPerKm(
  distanceM: number | null,
  durationS: number | null,
): Maybe<number, CardioUnknownReason> {
  if (distanceM === null) return unknown('missing_distance');
  if (durationS === null) return unknown('missing_duration');
  if (distanceM <= 0) return unknown('zero_distance');
  return known(Math.round((durationS / distanceM) * 1000));
}

/** Speed in km/h, rounded to two decimals. */
export function speedKph(
  distanceM: number | null,
  durationS: number | null,
): Maybe<number, CardioUnknownReason> {
  if (distanceM === null) return unknown('missing_distance');
  if (durationS === null || durationS <= 0) return unknown('missing_duration');
  return known(Math.round((distanceM / durationS) * 3.6 * 100) / 100);
}

/** Format a pace as 'M:SS /km'. Locale-independent by construction. */
export function formatPace(secondsPerKm: number): string {
  const total = Math.max(0, Math.round(secondsPerKm));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')} /km`;
}

/**
 * Machine-based cardio a numbered "Level" dial can plausibly map onto.
 *
 * Deliberately just these two: a gym floor has treadmills and stationary
 * bikes with Level dials, but nothing about a rowing machine, elliptical or
 * stair climber's own Level scale was researched for this feature, so they
 * are left alone rather than guessed at.
 */
export type CardioMachineKind = 'treadmill' | 'bike';

/**
 * A treadmill Level step, in km/h — LiftX's own simple default, not a real
 * manufacturer figure.
 *
 * There is no universal standard: a Level 5 on one treadmill brand might be
 * a brisk walk, on another a jog. 1.6 km/h per level is a plain, easy-to-
 * explain approximation loosely modelled on common basic/gym treadmill dials
 * (roughly walking pace at low levels, roughly jogging pace by level 5-6,
 * running by level 10+) — good enough to seed a starting value, never
 * claimed as accurate to any specific machine. The user's actual machine
 * reading always takes priority (`CardioSegmentRow`'s manual-override rule).
 */
const TREADMILL_KPH_PER_LEVEL = 1.6;

/**
 * LiftX's default Level → estimated speed. Returns `null` when there is no
 * reliable default to offer:
 *
 * - `kind === 'bike'` always returns `null` — a stationary bike's resistance
 *   Level does not correspond to a speed in any consistent way across
 *   manufacturers (unlike a treadmill, which is at least always a belt
 *   moving at a set speed). Forcing a number here would be false precision;
 *   the athlete enters their own estimate instead.
 * - A non-positive or non-finite `level` also returns `null`.
 */
export function estimateSpeedKphFromLevel(level: number, kind: CardioMachineKind): number | null {
  if (!Number.isFinite(level) || level <= 0) return null;
  if (kind === 'bike') return null;
  return Math.round(level * TREADMILL_KPH_PER_LEVEL * 10) / 10;
}

/**
 * Estimated distance from a speed estimate and elapsed time —
 * `distance = speed × time`, the same relationship `speedKph` inverts.
 *
 * This is explicitly the ESTIMATE side of the estimated/actual distinction:
 * callers show this only when the athlete has not logged a real `distanceM`
 * for the segment, and must stop showing it (in favour of the real value)
 * the moment one exists — see `CardioSegmentRow`'s own doc.
 */
export function estimateDistanceM(speedKph: number | null, durationS: number | null): number | null {
  if (speedKph === null || speedKph <= 0) return null;
  if (durationS === null || durationS <= 0) return null;
  return Math.round((speedKph / 3.6) * durationS);
}

export interface CardioSummary {
  segments: number;
  completedSegments: number;
  totalDurationS: number;
  totalDistanceM: number;
  totalCalories: number;
  /** Duration-weighted, so a long easy segment does not average out a sprint. */
  averageHr: number | null;
  maxHr: number | null;
  /** Derived from the totals, not averaged from per-segment paces. */
  pace: Maybe<number, CardioUnknownReason>;
  /** True when some completed segment lacked a metric being totalled. */
  isPartial: boolean;
}

/**
 * Aggregate cardio segments.
 *
 * Only completed segments contribute, matching the rule strength volume uses.
 */
export function summariseCardio(segments: readonly CardioSegment[]): CardioSummary {
  let totalDurationS = 0;
  let totalDistanceM = 0;
  let totalCalories = 0;
  let maxHr: number | null = null;
  let hrWeightedSum = 0;
  let hrWeightSeconds = 0;
  let completedSegments = 0;
  let isPartial = false;

  for (const segment of segments) {
    if (!segment.isCompleted) continue;
    completedSegments++;

    if (segment.durationS !== null && segment.durationS > 0) {
      totalDurationS += segment.durationS;
    } else {
      isPartial = true;
    }

    if (segment.distanceM !== null && segment.distanceM > 0) {
      totalDistanceM += segment.distanceM;
    }
    if (segment.calories !== null && segment.calories > 0) {
      totalCalories += segment.calories;
    }

    if (segment.maxHr !== null && (maxHr === null || segment.maxHr > maxHr)) {
      maxHr = segment.maxHr;
    }
    if (segment.avgHr !== null && segment.durationS !== null && segment.durationS > 0) {
      hrWeightedSum += segment.avgHr * segment.durationS;
      hrWeightSeconds += segment.durationS;
    }
  }

  return {
    segments: segments.length,
    completedSegments,
    totalDurationS,
    totalDistanceM,
    totalCalories,
    averageHr: hrWeightSeconds > 0 ? Math.round(hrWeightedSum / hrWeightSeconds) : null,
    maxHr,
    pace: paceSecondsPerKm(totalDistanceM > 0 ? totalDistanceM : null, totalDurationS),
    isPartial,
  };
}

export interface CardioValidationIssue {
  segmentId: string;
  code: 'hr_inverted' | 'negative_value' | 'incline_out_of_range';
  detail: string;
}

/** Structural checks the phone can apply before queueing a segment. */
export function validateCardioSegments(
  segments: readonly CardioSegment[],
): CardioValidationIssue[] {
  const issues: CardioValidationIssue[] = [];

  for (const segment of segments) {
    if (segment.avgHr !== null && segment.maxHr !== null && segment.maxHr < segment.avgHr) {
      issues.push({
        segmentId: segment.id,
        code: 'hr_inverted',
        detail: `maxHr ${String(segment.maxHr)} is below avgHr ${String(segment.avgHr)}`,
      });
    }
    for (const [field, value] of [
      ['durationS', segment.durationS],
      ['distanceM', segment.distanceM],
      ['calories', segment.calories],
    ] as const) {
      if (value !== null && value < 0) {
        issues.push({
          segmentId: segment.id,
          code: 'negative_value',
          detail: `${field} = ${String(value)}`,
        });
      }
    }
    if (segment.inclinePct !== null && (segment.inclinePct < -100 || segment.inclinePct > 100)) {
      issues.push({
        segmentId: segment.id,
        code: 'incline_out_of_range',
        detail: `incline = ${String(segment.inclinePct)}`,
      });
    }
  }

  return issues;
}
